import type { CaptureError } from '@shared/types';

/**
 * Translates DOMException / unknown errors thrown by getUserMedia into the
 * app's own CaptureError shape, so nothing outside this adapter needs to
 * know about DOMException's string-based `.name` field.
 */
export function toCaptureError(cause: unknown): CaptureError {
  if (cause instanceof DOMException) {
    switch (cause.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return {
          code: 'permission-denied',
          message: 'Camera or microphone access was denied.',
          cause,
        };
      case 'NotFoundError':
        return {
          code: 'device-not-found',
          message: 'Selected camera or microphone was not found.',
          cause,
        };
      case 'NotReadableError':
        return {
          code: 'device-in-use',
          message: 'Device is already in use by another application.',
          cause,
        };
      case 'OverconstrainedError':
        return {
          code: 'constraints-not-satisfiable',
          message: 'The selected resolution/frame rate is not supported by this device.',
          cause,
        };
      default:
        return { code: 'unknown', message: cause.message || 'Unknown capture error.', cause };
    }
  }

  if (cause instanceof Error) {
    return { code: 'unknown', message: cause.message, cause };
  }

  return { code: 'unknown', message: 'An unknown capture error occurred.', cause };
}
